/** A tiny typed element helper (no framework) and the disposal scope every view owns. */

export type Child = Node | string | null | false | undefined;

type Listeners = {
  [K in keyof HTMLElementEventMap]?: (event: HTMLElementEventMap[K]) => void;
};

export interface Props {
  readonly class?: string;
  readonly attrs?: Readonly<Record<string, string | null>>;
  readonly on?: Listeners;
}

/** Create an element with a class, attributes, listeners and children; strings become text nodes. */
export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: Props = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const element = document.createElement(tag);
  if (props.class !== undefined) {
    element.className = props.class;
  }
  for (const [name, value] of Object.entries(props.attrs ?? {})) {
    if (value !== null) {
      element.setAttribute(name, value);
    }
  }
  for (const [type, listener] of Object.entries(props.on ?? {})) {
    element.addEventListener(type, listener as EventListener);
  }
  append(element, children);
  return element;
}

/** Append children to a parent, skipping empty ones. */
export function append(parent: Node, children: readonly Child[]): void {
  for (const child of children) {
    if (child === null || child === false || child === undefined) {
      continue;
    }
    parent.appendChild(typeof child === "string" ? document.createTextNode(child) : child);
  }
}

/** Replace all children of a parent. */
export function setChildren(parent: Element, children: readonly Child[]): void {
  parent.replaceChildren();
  append(parent, children);
}

/** Owns the listeners, timers and subscriptions of one view; `dispose` releases them all. */
export class Scope {
  private readonly releasers: (() => void)[] = [];
  private closed = false;

  /** Whether `dispose` has run. */
  get disposed(): boolean {
    return this.closed;
  }

  /** The number of resources currently held. */
  get size(): number {
    return this.releasers.length;
  }

  /** Register a release function; it runs at once if the scope is already disposed. */
  add(release: () => void): void {
    if (this.closed) {
      release();
      return;
    }
    this.releasers.push(release);
  }

  /** Add an event listener that is removed when the scope is disposed. */
  listen(target: EventTarget, type: string, listener: EventListener): void {
    target.addEventListener(type, listener);
    this.add(() => {
      target.removeEventListener(type, listener);
    });
  }

  /** Start an interval that stops when the scope is disposed. */
  every(ms: number, task: () => void): void {
    const id = setInterval(task, ms);
    this.add(() => {
      clearInterval(id);
    });
  }

  /** Release everything, once. */
  dispose(): void {
    if (this.closed) {
      return;
    }
    this.closed = true;
    for (const release of this.releasers.splice(0).reverse()) {
      release();
    }
  }
}
